import { constructDefinitionSourceFromBase } from '../janus-engine/port/k1-compile/from-base/definition-source';
import { fromBaseRecord, FromBaseInputMissing, FromBaseStageMissing } from '../janus-engine/compile/from-base-context';
import type { FromBaseRow as Row } from '../janus-engine/compile/from-base-context';
import { constructInventoryDefinitionReferences } from '../janus-engine/compile/from-base-inventory-references';
import type { InventoryReferenceMetadata } from '../janus-engine/compile/from-base-inventory-references';
import { constructPlayerDefinitionPictures } from '../janus-engine/compile/from-base-player-media';
import { finalizeInventoryDefinitionLayerSurface } from '../janus-engine/compile/from-base-inventory-layer-surface';
import { normalizeInventoryDefinition } from '../janus-engine/compile/from-base-inventory-normalization';
import { normalizeFromBaseCatalog, FROM_BASE_STATIC_REGISTRIES } from '../janus-engine/compile/from-base-catalog-normalization';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import type { ProviderCatalogRows } from '../janus-engine/compile/provider-catalog-order';

export interface AncestryCardMetadata {
    layerCatalog: InventoryReferenceMetadata['layerCatalog'] & ProviderCatalogRows;
    abilityTypes: readonly string[];
    cdnUrl: string;
}

/** Complete only the card's saved item/media/passive/provider window. Reuse the
 * native reference depth, layer cleanup and catalog normalization; combat,
 * pricing, assets, choices and random-name tables cannot affect this surface. */
export function createAncestryCardFinalizer(base: Row, metadata: AncestryCardMetadata) {
    const catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
    const missing = new Set((base.missing_definition_ids as string[] ?? []).map(id => id.toUpperCase()));
    const rawDefinition = (id: string): Row | null => {
        const row = catalog[id] ?? catalog[id.toUpperCase()];
        if (fromBaseRecord(row)) return row;
        if (missing.has(id.toUpperCase())) return null;
        throw new FromBaseInputMissing('ancestry-card-definition', id);
    };
    const references: InventoryReferenceMetadata = { layerCatalog: metadata.layerCatalog, sizeCatalog: [],
        shellRegistriesComplete: true, shellRegistries: Object.fromEntries(Object.values(metadata.layerCatalog)
            .map(spec => spec.references_item_type).filter((type): type is string => !!type)
            .map(type => [type, FROM_BASE_STATIC_REGISTRIES[type] ?? null])) };
    const prepared = new Map<string, Row>();
    const active = new Set<string>();
    function definition(id: string, ancestors: readonly string[] = []): Row | null {
        if (!ancestors.length && prepared.has(id)) return prepared.get(id)!;
        const raw = rawDefinition(id);
        if (!raw) return null;
        const token = `${id}:${ancestors.join(',')}`;
        if (active.has(token)) throw new FromBaseStageMissing('catalog-lean-normalization', `definition-cycle:${id}`);
        active.add(token);
        try {
            let row = constructDefinitionSourceFromBase(raw);
            const pictures = constructPlayerDefinitionPictures(raw, metadata.cdnUrl, readItemField);
            if (pictures.length) row.pictures = { item: pictures };
            row = constructInventoryDefinitionReferences(row, references, rawDefinition, { ancestors, fullDefinition: definition });
            row = finalizeInventoryDefinitionLayerSurface(row);
            row = normalizeInventoryDefinition(row, metadata.layerCatalog);
            if (!ancestors.length) {
                row = normalizeFromBaseCatalog(row, { definition: id => definition(id), rawDefinition,
                    abilityTypes: metadata.abilityTypes });
                prepared.set(id, row);
            }
            return row;
        } finally { active.delete(token); }
    }
    return { definition };
}
